import React, { useState, useEffect } from 'react';
import { Building, ChevronDown, ChevronLeft, ChevronRight, Save, X, Edit, Check, Home } from 'lucide-react';
import { supabase } from '../../../../lib/supabase';
import '../../styles/RoomsDetails.css';

const RoomsDetails = () => {
  const [data, setData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [selectedRoom, setSelectedRoom] = useState('');
  const [sortBy, setSortBy] = useState('subject'); // 'subject' or 'class'
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(20);
  const [loading, setLoading] = useState(false);
  const [isModified, setIsModified] = useState(false);
  const [showHomeRoomAssignment, setShowHomeRoomAssignment] = useState(false);
  const [availableRooms, setAvailableRooms] = useState([]);

  // Load data from Supabase
  useEffect(() => {
    loadData();
  }, []);

  // Filter and sort data when sortBy changes
  useEffect(() => {
    let sorted = [...data];
    
    if (sortBy === 'subject') {
      sorted.sort((a, b) => a.subject_code.localeCompare(b.subject_code));
    } else {
      sorted.sort((a, b) => a.class_id.localeCompare(b.class_id));
    }
    
    setFilteredData(sorted);
  }, [data, sortBy]);

  const loadData = async () => {
    try {
      setLoading(true);
      const { data: roomsData, error } = await supabase
        .from('Class_Subject_Item_Load_Quota_Details_Rooms')
        .select('*')
        .eq('status', 'active')
        .order('class_id, subject_code, page_number');

      if (error) throw error;

      setData(roomsData || []);
      
      // Extract unique rooms for dropdown
      const rooms = [...new Set(roomsData?.map(item => item.room_code) || [])];
      setAvailableRooms(rooms);
      
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleItemSelect = (item) => {
    setSelectedItem(item);
    setSelectedRoom(item.room_code);
    setIsModified(false);
  };

  const handleRoomChange = (roomCode) => {
    setSelectedRoom(roomCode);
    setIsModified(true);
  };

  const handleModify = () => {
    if (!selectedItem) return;
    // Enable modification mode
    setIsModified(true);
  };

  const handleSave = async () => {
    if (!selectedItem || !selectedRoom) return;

    try {
      const { error } = await supabase
        .from('Class_Subject_Item_Load_Quota_Details_Rooms')
        .update({ 
          room_code: selectedRoom,
          is_modified: true,
          updated_at: new Date().toISOString()
        })
        .eq('id', selectedItem.id);

      if (error) throw error;

      // Update local data
      setData(prev => prev.map(item => 
        item.id === selectedItem.id 
          ? { ...item, room_code: selectedRoom, is_modified: true }
          : item
      ));

      setIsModified(false);
      alert('儲存成功 (Save successful)');
      
    } catch (error) {
      console.error('Error saving:', error);
      alert('儲存失敗 (Save failed)');
    }
  };

  const handleCancel = () => {
    setSelectedRoom(selectedItem?.room_code || '');
    setIsModified(false);
  };

  const handleHomeRoomAssignment = () => {
    setShowHomeRoomAssignment(true);
    // Auto-assign home rooms for all subjects
    const homeRoomAssignments = data.map(item => ({
      ...item,
      is_home_room: true,
      room_code: item.class_id === '1A' ? '101' : item.class_id === '1B' ? '102' : '101'
    }));
    
    setData(homeRoomAssignments);
    alert('原課室編配完成 (Home room assignment completed)');
  };

  const handleNextPage = () => {
    const totalPages = Math.ceil(filteredData.length / itemsPerPage);
    if (currentPage < totalPages) {
      setCurrentPage(currentPage + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(currentPage - 1);
    }
  };

  const getCurrentPageData = () => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return filteredData.slice(startIndex, endIndex);
  };

  const getDisplayName = (item) => {
    return `${item.class_id}--${item.subject_code}--Page ${item.page_number}--${item.room_code}`;
  };

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  return (
    <div className="rooms-details-container">
      {/* Header */}
      <div className="rooms-header">
        <h2>Class-Subject Item Quota Details - Rooms (TBM0182E)</h2>
      </div>

      <div className="rooms-content">
        {/* Left Panel - Data List */}
        <div className="rooms-left-panel">
          {/* Sort Dropdown */}
          <div className="sort-container">
            <label>排序方式 (Sort by):</label>
            <div className="select-container">
              <div className="select-wrapper">
                <select 
                  value={sortBy} 
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  <option value="subject">Subject</option>
                  <option value="class">Class</option>
                </select>
                <div className="select-arrow">
                  <ChevronDown size={16} />
                </div>
              </div>
            </div>
          </div>

          {/* Data List */}
          <div className="data-list-container">
            <div className="list-header">Class-SubjItem-PageNo-Room</div>
            <div className="data-list">
              {getCurrentPageData().map((item, index) => (
                <div
                  key={item.id}
                  className={`data-item ${selectedItem?.id === item.id ? 'selected' : ''} ${item.is_modified ? 'modified' : ''}`}
                  onClick={() => handleItemSelect(item)}
                >
                  {getDisplayName(item)}
                </div>
              ))}
            </div>
            
            {/* Pagination */}
            <div className="pagination">
              <span>{currentPage}/{totalPages}</span>
            </div>
          </div>
        </div>

        {/* Right Panel - Controls */}
        <div className="rooms-right-panel">
          {/* Selected Item Display */}
          <div className="selected-section">
            <label>Selected</label>
            <div className="selected-display">
              {selectedItem ? getDisplayName(selectedItem) : 'No item selected'}
            </div>
          </div>

          {/* Room Selection */}
          <div className="room-section">
            <label>Room</label>
            <div className="room-input-container">
              <input
                type="text"
                value={selectedRoom}
                onChange={(e) => handleRoomChange(e.target.value)}
                className="room-input"
                disabled={!selectedItem}
              />
              <div className="select-container">
                <div className="select-wrapper">
                  <select
                    value={selectedRoom}
                    onChange={(e) => handleRoomChange(e.target.value)}
                    disabled={!selectedItem}
                  >
                    <option value="">Select Room</option>
                    {availableRooms.map(room => (
                      <option key={room} value={room}>{room}</option>
                    ))}
                  </select>
                  <div className="select-arrow">
                    <ChevronDown size={16} />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Navigation Buttons */}
          <div className="nav-buttons">
            <button 
              className="nav-btn"
              onClick={handlePrevPage}
              disabled={currentPage === 1}
            >
              <ChevronLeft size={16} />
              Prev Page
            </button>
            <button 
              className="nav-btn"
              onClick={handleNextPage}
              disabled={currentPage === totalPages}
            >
              Next Page
              <ChevronRight size={16} />
            </button>
          </div>

          {/* Action Buttons */}
          <div className="action-buttons">
            <button 
              className="action-btn modify-btn"
              onClick={handleModify}
              disabled={!selectedItem}
            >
              <Edit size={16} />
              Modify
            </button>
            <button 
              className="action-btn cancel-btn"
              onClick={handleCancel}
              disabled={!isModified}
            >
              <X size={16} />
              Cancel
            </button>
            <button 
              className="action-btn save-btn"
              onClick={handleSave}
              disabled={!isModified}
            >
              <Save size={16} />
              Save
            </button>
          </div>

          {/* Home Room Assignment */}
          <div className="home-room-section">
            <label>Home Room</label>
            <button 
              className="home-room-btn"
              onClick={handleHomeRoomAssignment}
            >
              <Home size={16} />
              Assignment
            </button>
          </div>
        </div>
      </div>

      {/* Loading Overlay */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner">載入中...</div>
        </div>
      )}
    </div>
  );
};

export default RoomsDetails;